/**
 * HRDocumentTemplatesTab — Page d'administration des modèles de documents RH.
 * Layout : liste à gauche, éditeur + panneau variables à droite.
 */
import React, { useEffect, useState, useCallback } from 'react';
import {
  FileText, Plus, Trash2, Copy, Save, Eye, AlertCircle,
  Loader2, CheckCircle2,
} from 'lucide-react';
import LoadingSpinner from '../../LoadingSpinner';
import { useToast } from '../../../contexts/NotificationContext';
import {
  getDocumentTemplates,
  getDocumentTemplateById,
  createDocumentTemplate,
  updateDocumentTemplate,
  deleteDocumentTemplate,
  duplicateDocumentTemplate,
  getDocumentTemplateVariables,
  previewDocumentTemplate,
} from '../../../api';
import DocumentTemplateEditor from './DocumentTemplateEditor';

const TYPE_LABELS = {
  offer_letter: 'Promesse',
  cdi: 'CDI',
  cdd: 'CDD',
  trial_extension: 'Période d\'essai',
  amendment: 'Avenant',
  custom: 'Autre',
};

const TYPE_COLORS = {
  offer_letter: 'bg-teal-50 text-teal-700 border-teal-200',
  cdi: 'bg-violet-50 text-violet-700 border-violet-200',
  cdd: 'bg-amber-50 text-amber-700 border-amber-200',
  trial_extension: 'bg-orange-50 text-orange-700 border-orange-200',
  amendment: 'bg-blue-50 text-blue-700 border-blue-200',
  custom: 'bg-gray-50 text-gray-700 border-gray-200',
};

export default function HRDocumentTemplatesTab() {
  const toast = useToast();
  const [templates, setTemplates] = useState([]);
  const [variables, setVariables] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedId, setSelectedId] = useState(null);
  const [draft, setDraft] = useState(null);  // {id, title, type, bodyHtml}
  const [saving, setSaving] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [previewHtml, setPreviewHtml] = useState('');
  const [showNew, setShowNew] = useState(false);
  const [newPayload, setNewPayload] = useState({ title: '', type: 'custom' });

  const loadList = useCallback(async () => {
    const res = await getDocumentTemplates({ includeInactive: false });
    setTemplates(res.data || []);
    return res.data || [];
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const [list, varsRes] = await Promise.all([
          loadList(),
          getDocumentTemplateVariables(),
        ]);
        setVariables(varsRes.data || []);
        if (list.length && !selectedId) setSelectedId(list[0].id);
      } catch (e) {
        toast.error('Erreur de chargement des modèles');
      } finally {
        setLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!selectedId) { setDraft(null); return; }
    (async () => {
      try {
        const res = await getDocumentTemplateById(selectedId);
        setDraft(res.data);
      } catch {
        toast.error('Erreur de chargement du modèle');
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedId]);

  const handleSave = async () => {
    if (!draft) return;
    setSaving(true);
    try {
      const payload = {
        title: draft.title,
        type: draft.type,
        bodyHtml: draft.bodyHtml,
        structureId: draft.structureId || null,
      };
      await updateDocumentTemplate(draft.id, payload);
      toast.success('Modèle enregistré');
      await loadList();
    } catch {
      toast.error('Erreur d\'enregistrement');
    } finally {
      setSaving(false);
    }
  };

  const handleDuplicate = async (id) => {
    try {
      const res = await duplicateDocumentTemplate(id);
      await loadList();
      setSelectedId(res.data.id);
      toast.success('Modèle dupliqué');
    } catch {
      toast.error('Erreur lors de la duplication');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Supprimer ce modèle ? (les documents déjà générés ne seront pas affectés)')) return;
    try {
      await deleteDocumentTemplate(id);
      const list = await loadList();
      if (selectedId === id) setSelectedId(list[0]?.id || null);
      toast.success('Modèle supprimé');
    } catch {
      toast.error('Erreur lors de la suppression');
    }
  };

  const handleCreate = async () => {
    if (!newPayload.title.trim()) {
      toast.error('Titre requis');
      return;
    }
    try {
      const res = await createDocumentTemplate({
        title: newPayload.title.trim(),
        type: newPayload.type,
        bodyHtml: '<p></p>',
      });
      await loadList();
      setSelectedId(res.data.id);
      setShowNew(false);
      setNewPayload({ title: '', type: 'custom' });
      toast.success('Modèle créé');
    } catch {
      toast.error('Erreur lors de la création');
    }
  };

  const handlePreview = async () => {
    if (!draft) return;
    try {
      const res = await previewDocumentTemplate({ bodyHtml: draft.bodyHtml });
      setPreviewHtml(res.data);
      setShowPreview(true);
    } catch {
      toast.error('Erreur lors de l\'aperçu');
    }
  };

  if (loading) {
    return (
      <div className="p-8 flex justify-center">
        <LoadingSpinner size="large" text="Chargement des modèles..." />
      </div>
    );
  }

  return (
    <div className="flex h-[calc(100vh-180px)] gap-4 p-4">
      {/* Liste à gauche */}
      <div className="w-72 flex flex-col bg-white border rounded-lg overflow-hidden">
        <div className="px-3 py-2 border-b bg-gray-50 flex items-center justify-between">
          <div className="text-sm font-semibold text-gray-700">Modèles</div>
          <button
            type="button"
            onClick={() => setShowNew(true)}
            className="p-1 text-primary hover:bg-primary/10 rounded"
            title="Créer un nouveau modèle"
          >
            <Plus size={16} />
          </button>
        </div>
        <div className="flex-1 overflow-auto">
          {templates.length === 0 && (
            <div className="p-4 text-sm text-gray-500 text-center">
              Aucun modèle pour l'instant.
            </div>
          )}
          {templates.map((t) => (
            <button
              key={t.id}
              type="button"
              onClick={() => setSelectedId(t.id)}
              className={`w-full text-left px-3 py-2 border-b transition ${
                selectedId === t.id
                  ? 'bg-primary/10 border-l-2 border-l-primary'
                  : 'hover:bg-gray-50'
              }`}
            >
              <div className="flex items-start gap-2">
                <FileText size={14} className="text-gray-400 mt-0.5 flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium text-gray-800 truncate">{t.title}</div>
                  <div className="mt-0.5 flex items-center gap-1">
                    <span className={`text-[9px] px-1.5 py-0.5 rounded border ${TYPE_COLORS[t.type] || TYPE_COLORS.custom}`}>
                      {TYPE_LABELS[t.type] || t.type}
                    </span>
                    {t.structure && (
                      <span className="text-[10px] text-gray-400">· {t.structure.name}</span>
                    )}
                  </div>
                </div>
              </div>
            </button>
          ))}
        </div>
      </div>

      {/* Éditeur à droite */}
      <div className="flex-1 flex flex-col">
        {!draft ? (
          <div className="flex-1 flex items-center justify-center text-gray-400">
            Sélectionne un modèle ou crée-en un nouveau.
          </div>
        ) : (
          <>
            {/* En-tête modèle */}
            <div className="flex items-center gap-3 mb-3">
              <input
                type="text"
                value={draft.title}
                onChange={(e) => setDraft((d) => ({ ...d, title: e.target.value }))}
                className="flex-1 px-3 py-2 border rounded-lg text-base font-semibold focus:ring-1 focus:ring-primary focus:border-primary"
                placeholder="Titre du modèle"
              />
              <select
                value={draft.type}
                onChange={(e) => setDraft((d) => ({ ...d, type: e.target.value }))}
                className="px-3 py-2 border rounded-lg text-sm"
              >
                {Object.entries(TYPE_LABELS).map(([k, v]) => (
                  <option key={k} value={k}>{v}</option>
                ))}
              </select>
              <button
                type="button"
                onClick={() => handleDuplicate(draft.id)}
                className="px-3 py-2 text-sm text-gray-600 hover:bg-gray-100 rounded-lg inline-flex items-center gap-1"
                title="Dupliquer"
              >
                <Copy size={14} /> Dupliquer
              </button>
              <button
                type="button"
                onClick={() => handleDelete(draft.id)}
                className="px-3 py-2 text-sm text-red-600 hover:bg-red-50 rounded-lg inline-flex items-center gap-1"
                title="Supprimer"
              >
                <Trash2 size={14} /> Supprimer
              </button>
              <button
                type="button"
                onClick={handleSave}
                disabled={saving}
                className="px-4 py-2 text-sm bg-primary text-white hover:bg-primary-dark rounded-lg inline-flex items-center gap-1.5 disabled:opacity-60"
              >
                {saving ? (
                  <Loader2 size={14} className="animate-spin" />
                ) : (
                  <Save size={14} />
                )}
                Enregistrer
              </button>
            </div>

            {/* Éditeur + panneau */}
            <div className="flex-1 min-h-0">
              <DocumentTemplateEditor
                content={draft.bodyHtml}
                onChange={(html) => setDraft((d) => ({ ...d, bodyHtml: html }))}
                variables={variables}
                onPreview={handlePreview}
              />
            </div>
          </>
        )}
      </div>

      {/* Modale création */}
      {showNew && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md p-5">
            <h3 className="text-lg font-semibold mb-4">Nouveau modèle</h3>
            <div className="space-y-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Titre</label>
                <input
                  type="text"
                  value={newPayload.title}
                  onChange={(e) => setNewPayload((p) => ({ ...p, title: e.target.value }))}
                  className="w-full px-3 py-2 border rounded-lg"
                  placeholder="ex: Contrat alternance"
                  autoFocus
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Type</label>
                <select
                  value={newPayload.type}
                  onChange={(e) => setNewPayload((p) => ({ ...p, type: e.target.value }))}
                  className="w-full px-3 py-2 border rounded-lg"
                >
                  {Object.entries(TYPE_LABELS).map(([k, v]) => (
                    <option key={k} value={k}>{v}</option>
                  ))}
                </select>
              </div>
            </div>
            <div className="flex justify-end gap-2 mt-5">
              <button
                type="button"
                onClick={() => { setShowNew(false); setNewPayload({ title: '', type: 'custom' }); }}
                className="px-4 py-2 text-sm text-gray-600 hover:bg-gray-100 rounded-lg"
              >
                Annuler
              </button>
              <button
                type="button"
                onClick={handleCreate}
                className="px-4 py-2 text-sm bg-primary text-white hover:bg-primary-dark rounded-lg"
              >
                Créer
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modale aperçu */}
      {showPreview && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-4xl h-[85vh] flex flex-col">
            <div className="flex items-center justify-between px-4 py-2 border-b">
              <div className="flex items-center gap-2 text-sm font-semibold">
                <Eye size={14} />
                Aperçu (valeurs réelles renseignées à la génération)
              </div>
              <button
                type="button"
                onClick={() => setShowPreview(false)}
                className="text-gray-500 hover:text-gray-700"
              >
                ✕
              </button>
            </div>
            <iframe
              title="aperçu"
              srcDoc={previewHtml}
              className="flex-1 w-full"
              sandbox=""
            />
          </div>
        </div>
      )}
    </div>
  );
}
